<template>
	<div class="px-6 lg:px-0">
		<Category
			v-if="currentCategory && initialData"
			:current-category="currentCategory"
			:initial-data="initialData"
			:pre-footer="preFooter"
			:story-data="storyblokDataRef?.content"
			:special-price="specialPrice"
			:advertisement="advertisement"
			:page-configuration="pageConfiguration"
			:recommendations="recommendations"
			:initial-page-no="initialPageNo"
		/>
		<div v-else class="main-wrapper flex flex-col gap-4 mb-10">
			<div class="border-b border-b-gray-lighter pb-6 h-[155px]">
				<h1
					class="text-3xl mb-4 animate-pulse bg-gray-lighter w-[100px] h-10"
				></h1>
				<span
					class="max-w-screen-sm block animate-pulse bg-gray-lighter w-[50px] h-10"
				></span>
			</div>
			<div class="flex-col lg:flex-row justify-between w-full flex">
				<div
					class="lg:w-[280px] relative h-[400px] gap-2 hidden md:flex flex-col"
				>
					<div
						v-for="i in 6"
						:key="i"
						class="h-[50px] bg-gray-light border-gray-lighter border animate-pulse w-full"
					/>
				</div>
				<div class="flex flex-wrap relative flex-1 lg:ml-8">
					<div class="grid grid-cols-2 lg:grid-cols-3 gap-8 w-full">
						<div
							v-for="i in 6"
							:key="i"
							class="h-[300px] md:h-[400px] bg-gray-light border-gray-lighter border animate-pulse w-full"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import type { Category as CategoryType } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	BlokSEOApp,
	SpecialPrice,
	BlokColumns,
	AdvertisementBlok,
	BlokProductRecommendations
} from "~/types/storyblok/storyblok";
import type {
	InitialData,
	PageConfiguration
} from "~/types/components/category";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { findDeepestObjectKey } from "~/utils/categories/findDeepestObjectKey";
import { activeStatusID } from "~/utils/constants";
import { normalizeProductSort } from "~/utils/product/sort";
import { omitHiddenFilterSections } from "~/utils/categories/filterSections";

const { fetchStory } = useStoryblokFetcher();
const { locale, localeProperties } = useI18n();
const { showPageNotFound } = usePageNotFound();
const norceStore = useNorceStore();
const storyblokLanguage = useStoryblokLanguage();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();

const config = useRuntimeConfig();
const ImagesCdnUrl = config.public.norce.cdn + "/";

const { getCategoryDetailsById, iterateOverCategoryDetails } = useCategory();
const pageStore = usePageStore();
const { setCategoryBreadcrumb, setPageType, setPageTitle } = pageStore;
const { categoryBreadcrumb } = storeToRefs(pageStore);
const { listProductByFilters, getCurrentCategory, mapProductFilters } =
	useNorcePwa();
const { fetchCategoryTree } = useFetchCategoryTree();
const { fetchAllParametrics } = useAllParametrics();

const initialData = ref<InitialData>();
const currentCategory = ref<CategoryType>();
const seo = ref<BlokSEOApp>();
const pageConfiguration = ref<PageConfiguration>({
	pageSize: "0",
	pictureAspectRatio: "4/3",
	initialCurrentSortingOrder: normalizeProductSort(route.query.sort),
	showPagination: false,
	rootParentCode: null
});
const storyblokDataRef = ref();
const layoutHiddenFilters = ref<unknown>();
// Live-sync the per-category `categories-content` story with the Storyblok
// Visual Editor (no-op outside the editor). Registered before the awaits
// below so the onMounted hook lands while the instance is still active;
// `storyblokDataRef` is populated later by the awaited fetch.
useStoryblokBridgeStory(storyblokDataRef, { resolveLinks: "url" });
const specialPrice = ref<SpecialPrice | null>(null);
const advertisement = ref<AdvertisementBlok | null>(null);
const recommendations = ref<BlokProductRecommendations | null>(null);
const initialPageNo = ref<number>(1);

const fetchCategoryData = async () => {
	if (!locale.value) {
		return;
	}

	setPageType("category");

	const categoryTree = await fetchCategoryTree(locale.value);
	const fetchedCategory = await getCurrentCategory(
		{
			categoriesSlug: route.params.slug as string[]
		},
		{ categoryTree, currentCategoryId: useCategoryStore().getCategoryId! }
	);

	if (!fetchedCategory) {
		setPageType("");

		return showPageNotFound();
	}

	/*
		The canonical path is the whole chain, not the leaf.

		`urls` stores a category as `parent/child`, composed up the tree, so
		pushing a single kebab-cased name sent the visitor to a path the index has
		never contained — a guaranteed 404 for anything nested. The chain is built
		from the tree already fetched above, the same way the feed builds it.

		An empty chain means the tree could not place this category, in which case
		nothing is pushed: a wrong redirect is worse than none.
	*/
	const canonicalPath = iterateOverCategoryDetails(
		getCategoryDetailsById(
			categoryTree,
			fetchedCategory.CategoryId!,
			"CategoryId"
		)
	);
	const requestedPath = ((route.params.slug as string[]) ?? []).join("/");

	if (canonicalPath && canonicalPath !== requestedPath) {
		setPageType("");
		router.push(localePath(`/${canonicalPath}`));
	}
	currentCategory.value = fetchedCategory;

	const breadcrumbCategory = getCategoryDetailsById(
		categoryTree,
		fetchedCategory.CategoryId,
		"CategoryId"
	);
	setCategoryBreadcrumb(breadcrumbCategory);
	setPageTitle(fetchedCategory.Name);
	setPageType("category");

	useCategoryStore().setCategoryId(currentCategory.value.CategoryId);
	const rootCategory = findDeepestObjectKey(breadcrumbCategory);
	pageConfiguration.value.rootParentCode = rootCategory
		? rootCategory.Code
		: null;
};

const fetchProductsData = async () => {
	if (!locale.value || !currentCategory.value) {
		return;
	}

	// Load-more mode deep-links via `?p`; pagination mode keeps `?pageNo`.
	const requestedPage = Math.max(1, parseInt(route.query.p as string, 10) || 1);
	initialPageNo.value = pageConfiguration.value.showPagination
		? 1
		: requestedPage;

	const allParametrics = await fetchAllParametrics();
	const [filters, products] = await Promise.all([
		mapProductFilters(
			{
				categorySeed: currentCategory.value.CategoryId?.toString(),
				// Same status as the listing, or facet counts include inactive products.
				statusSeed: String(activeStatusID),
				cultureCode: localeProperties.value.iso
			},
			{
				allParametrics
			}
		),
		listProductByFilters({
			categorySeed: currentCategory.value.CategoryId?.toString(),
			pageNo: pageConfiguration.value.showPagination
				? (route.query.pageNo as string) || "1"
				: String(requestedPage),
			pageSize: pageConfiguration.value.pageSize,
			sort: pageConfiguration.value.initialCurrentSortingOrder,
			statusSeed: String(activeStatusID),
			cultureCode: localeProperties.value.iso,
			...(route.query.filter ? { filter: route.query.filter as string } : {})
		})
	]);

	initialData.value = { products, ...filters };
};

const preFooter = ref<BlokColumns[]>([]);
const getInitialDataFromStoryBlok = async () => {
	const { data } = await fetchStory("layouts/category-page", {
		version: config.public.storyblokVersion,
		resolve_links: "url",
		language: storyblokLanguage.value
	});

	layoutHiddenFilters.value = data.story.content.hidden_filters;
	preFooter.value = data.story.content.pre_footer;
	pageConfiguration.value.pageSize = data.story.content.products_page_count
		? String(data.story.content.products_page_count)
		: "12";
	pageConfiguration.value.pictureAspectRatio =
		data.story.content.images_aspect_ratio;
	seo.value = data.story.content.seo;
	specialPrice.value = {
		background: data.story.content.special_price_background_color?.value || "",
		text: data.story.content.special_price_text_color?.value || "",
		enabled: !!data.story.content.show_special_price_end_date
	};
	pageConfiguration.value.showPagination = data.story.content.show_pagination;
	advertisement.value = data.story.content.advertisement?.[0];
	recommendations.value = data.story.content.recommendations?.[0];
};

const storyblokSeo = computed(() => storyblokDataRef.value?.content?.SEO);

const seoImage = computed(() => {
	if (currentCategory.value?.ImageKey) {
		return ImagesCdnUrl + currentCategory.value?.ImageKey;
	} else if (storyblokSeo.value?.og_image) {
		return storyblokSeo.value.og_image;
	} else if (seo.value?.og_image) {
		return seo.value?.og_image;
	}

	return null;
});

const seoFields = computed(() => {
	const defaultTitle = `${currentCategory.value?.Name}`;

	const {
		title: sbTitle,
		og_title: sbOgTitle,
		description: sbDescription,
		og_description: sbOgDescription
	} = storyblokSeo.value || {};

	return {
		title: sbTitle || defaultTitle,
		ogTitle: sbOgTitle || sbTitle || defaultTitle,
		description: sbDescription || seo.value?.description,
		ogDescription: sbOgDescription || seo.value?.og_description,
		ogImage: seoImage.value
	};
});

const fetchNorceData = async () => {
	await fetchCategoryData();

	if (currentCategory.value?.CategoryId) {
		try {
			const { data } = await fetchStory(
				`${config.public.categoryContentKey}/${currentCategory.value.CategoryId}`,
				{
					version: config.public.storyblokVersion,
					language: storyblokLanguage.value,
					resolve_links: "url"
				}
			);
			storyblokDataRef.value = data?.story;
		} catch {
			storyblokDataRef.value = null;
		}
	}

	if (!storyblokDataRef.value?.content?.hide_products) {
		await fetchProductsData();
	} else {
		initialData.value = { products: [], filters: [] };
	}
};

/**
 * The two phases above used to run as bare `await`s in setup, so hydration
 * repeated every one of them in the browser: the layout story, the category
 * tree, all parametrics, the category story, the filters and the product list.
 * Running them inside `useAsyncData` means the server result travels in the
 * Nuxt payload and the client reuses it.
 *
 * The phases themselves are unchanged — they still assign the same refs. What
 * is new is the snapshot: when the payload is reused the handler never runs on
 * the client, so the refs would stay empty. `captureSnapshot` reads them after
 * the fetch and `applySnapshot` writes them back.
 */
const captureSnapshot = () => ({
	currentCategory: currentCategory.value,
	initialData:
		initialData.value &&
		omitHiddenFilterSections(
			initialData.value,
			layoutHiddenFilters.value,
			storyblokDataRef.value?.content?.hidden_filters
		),
	seo: seo.value,
	// Copied, not referenced: the object is mutated in place on the client.
	pageConfiguration: { ...pageConfiguration.value },
	storyblokDataRef: storyblokDataRef.value,
	specialPrice: specialPrice.value,
	advertisement: advertisement.value,
	recommendations: recommendations.value,
	initialPageNo: initialPageNo.value,
	preFooter: preFooter.value
});

const applySnapshot = (snapshot: ReturnType<typeof captureSnapshot>) => {
	currentCategory.value = snapshot.currentCategory;
	initialData.value = snapshot.initialData;
	seo.value = snapshot.seo;
	// `Object.assign` rather than replacing the ref's value: the Category
	// component receives this object as a prop and reads it on mount.
	Object.assign(pageConfiguration.value, snapshot.pageConfiguration);
	storyblokDataRef.value = snapshot.storyblokDataRef;
	specialPrice.value = snapshot.specialPrice;
	advertisement.value = snapshot.advertisement;
	recommendations.value = snapshot.recommendations;
	initialPageNo.value = snapshot.initialPageNo;
	preFooter.value = snapshot.preFooter;
};

// The sales area belongs in the key, not only in the watch: the shopper's area
// lives in the browser, so the server renders with the application's default
// and the browser may hold the country chosen at checkout. `useAsyncData` reuses
// the server's payload whenever the key matches, so without this the listing
// keeps the prices rendered before a country was known.

const { data: categorySnapshot, error: categoryError } = await useAsyncData(
	`category:${route.path}:${norceStore.priceContextKey}`,
	async () => {
		await getInitialDataFromStoryBlok();
		await fetchNorceData();

		return captureSnapshot();
	},
	{ watch: [locale] }
);

// A failed fetch is logged, not turned into an error page: `useAsyncData`
// captures the throw into `error`, and without this the page would sit on the
// loading skeleton with nothing in the console. A genuinely missing category
// still gets a real 404, from `fetchCategoryData`.
if (categoryError.value) {
	console.error("[category] failed to load:", categoryError.value);
}

watch(
	categorySnapshot,
	(snapshot) => {
		if (snapshot) applySnapshot(snapshot);
	},
	{ immediate: true }
);

const requestURL = useRequestURL();
const domain = computed(() => requestURL.origin);

const productItemList = computed(() => {
	if (!initialData.value?.products?.Items?.length) {
		return null;
	}

	// The products on this page, counted as listed: `numberOfItems` must match
	// `itemListElement`, not the category total (ItemCount spans every page).
	const pageItems = initialData.value.products.Items;
	return defineItemList({
		numberOfItems: pageItems.length,
		itemListElement: pageItems.map((product, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: product.Name || product.VariantName || "",
			url: product.UniqueName
				? `${domain.value}${localePath(`/${product.UniqueName}`)}`
				: undefined
		}))
	});
});

useSeoMeta({
	title: () => seoFields.value.title,
	ogTitle: () => seoFields.value.ogTitle,
	description: () => seoFields.value.description,
	ogDescription: () => seoFields.value.ogDescription
});
if (import.meta.server) {
	const appStore = useAppStore();
	// No image is fine: OgCategory renders a text card without one.
	defineOgImageComponent("OgCategory", {
		image: seoImage.value || undefined,
		logo: appStore.siteLogo?.filename || "",
		title: seoFields.value.ogTitle || "",
		description: seoFields.value.description || "",
		width: 1200,
		height: 630
	});
}

const { buildBreadcrumbItems } = useCategoryBreadcrumb();

useSchemaOrg(
	computed(() => {
		const breadcrumbItems = buildBreadcrumbItems(categoryBreadcrumb.value);
		return [
			defineWebPage({
				"@type": "CollectionPage",
				name: currentCategory.value?.Name || "",
				description: seoFields.value.description || "",
				image: seoImage.value || undefined,
				...(productItemList.value ? { mainEntity: productItemList.value } : {})
			}),
			...(breadcrumbItems.length > 1
				? [defineBreadcrumb({ itemListElement: breadcrumbItems })]
				: [])
		];
	})
);

// Both watchers that used to sit here are gone. Locale is covered by
// `useAsyncData`'s `watch` above. Page size watched `pageConfiguration.pageSize`,
// which only ever changes when the layout story is read — and this template has
// no page-size selector (its one `ElSelect` is bound to the sorting order), so
// the watcher never fired for a shopper. Keeping it would have been actively
// harmful after this change: `applySnapshot` writes the page size on the client,
// which would have re-run the whole fetch and reintroduced the duplicate load
// this refactor removes.
</script>
